import { shallowRef } from 'vue'
import * as THREE from 'three'

export type RenderStats = {
  fps: number
  frameMs: number
  drawCalls: number
  triangles: number
}

const STATS_PUBLISH_MS = 250

export type RenderContext = {
  renderer: THREE.WebGLRenderer
  scene: THREE.Scene
  camera: THREE.PerspectiveCamera
}

export type FrameTask = (now: number) => boolean

const IDLE_PIXEL_RATIO = 2
const MIN_INTERACTION_RATIO = 1
const MAX_INTERACTION_RATIO = IDLE_PIXEL_RATIO
const SLOW_FRAME_MS = 1000 / 45
const FAST_FRAME_MS = 1000 / 85
const FRAMES_BETWEEN_ADJUSTMENTS = 8
const MIN_RATIO_STEP = 0.1
const MAX_DRAWING_BUFFER_PIXELS = 4_500_000

export function useRenderLoop(getContext: () => RenderContext | null) {
  const tasks = new Set<FrameTask>()
  const worldFlushTasks = new Set<() => void>()
  let rafId: number | null = null
  let frameRequested = false
  let worldDirty = true
  let continuousWorldUpdates = false

  let interacting = false
  let interactionRatio = MIN_INTERACTION_RATIO
  let frameTimeEma: number | null = null
  let lastFrameAt: number | null = null
  let framesSinceAdjustment = 0

  const stats = shallowRef<RenderStats>({ fps: 0, frameMs: 0, drawCalls: 0, triangles: 0 })
  let statsPublishedAt = 0
  const sizeScratch = new THREE.Vector2()

  function loop() {
    frameRequested = false
    rafId = null
    const ctx = getContext()
    if (!ctx) return

    const now = performance.now()
    let moving = false
    for (const task of tasks) {
      if (task(now)) moving = true
    }

    flushWorldMatrices(ctx)
    ctx.renderer.render(ctx.scene, ctx.camera)
    trackFrameTime(ctx, now)
    if (moving) requestRender()
  }

  function trackFrameTime(ctx: RenderContext, now: number) {
    const previous = lastFrameAt
    lastFrameAt = now
    if (previous === null) return

    const delta = now - previous
    frameTimeEma = frameTimeEma === null ? delta : frameTimeEma * 0.8 + delta * 0.2
    publishStats(ctx, now)

    if (!interacting) return
    if (++framesSinceAdjustment < FRAMES_BETWEEN_ADJUSTMENTS) return
    framesSinceAdjustment = 0

    if (frameTimeEma > SLOW_FRAME_MS) applyInteractionRatio(ctx, interactionRatio * 0.8)
    else if (frameTimeEma < FAST_FRAME_MS) applyInteractionRatio(ctx, interactionRatio * 1.25)
  }

  function publishStats(ctx: RenderContext, now: number) {
    if (now - statsPublishedAt < STATS_PUBLISH_MS) return
    statsPublishedAt = now
    const frameMs = frameTimeEma ?? 0
    stats.value = {
      frameMs,
      fps: frameMs > 0 ? 1000 / frameMs : 0,
      drawCalls: ctx.renderer.info.render.calls,
      triangles: ctx.renderer.info.render.triangles,
    }
  }

  function applyInteractionRatio(ctx: RenderContext, ratio: number) {
    const next = Math.min(Math.max(ratio, MIN_INTERACTION_RATIO), MAX_INTERACTION_RATIO)
    if (Math.abs(next - interactionRatio) < MIN_RATIO_STEP) return
    interactionRatio = next
    setPixelRatio(ctx, next)
    frameTimeEma = null
    requestRender()
  }

  function setPixelRatio(ctx: RenderContext, ratio: number) {
    ctx.renderer.getSize(sizeScratch)
    const area = sizeScratch.x * sizeScratch.y
    const budgeted = area > 0 ? Math.sqrt(MAX_DRAWING_BUFFER_PIXELS / area) : ratio
    const affordable = Math.max(budgeted, MIN_INTERACTION_RATIO)
    const target = Math.min(window.devicePixelRatio, ratio, affordable)
    if (ctx.renderer.getPixelRatio() === target) return
    ctx.renderer.setPixelRatio(target)
  }

  function refreshPixelRatio() {
    const ctx = getContext()
    if (!ctx) return
    setPixelRatio(ctx, interacting ? interactionRatio : IDLE_PIXEL_RATIO)
  }

  function flushWorldMatrices(ctx: RenderContext | null = getContext()) {
    if (!ctx) return
    if (!worldDirty && !continuousWorldUpdates) return
    ctx.scene.updateMatrixWorld()
    for (const task of worldFlushTasks) task()
    worldDirty = false
  }

  function requestRender() {
    if (frameRequested || !getContext()) return
    frameRequested = true
    rafId = window.requestAnimationFrame(loop)
  }

  function renderNow() {
    if (rafId !== null) window.cancelAnimationFrame(rafId)
    loop()
  }

  function markSceneChanged() {
    worldDirty = true
    requestRender()
  }

  function setContinuousWorldUpdates(enabled: boolean) {
    continuousWorldUpdates = enabled
    if (enabled) markSceneChanged()
  }

  function setInteracting(next: boolean) {
    const ctx = getContext()
    if (!ctx || interacting === next) return
    interacting = next
    frameTimeEma = null
    lastFrameAt = null
    framesSinceAdjustment = 0
    setPixelRatio(ctx, next ? interactionRatio : IDLE_PIXEL_RATIO)
    requestRender()
  }

  function addFrameTask(task: FrameTask): () => void {
    tasks.add(task)
    return () => tasks.delete(task)
  }

  /** Runs after world matrices were brought up to date, i.e. after anything in the scene changed. */
  function addWorldFlushTask(task: () => void): () => void {
    worldFlushTasks.add(task)
    return () => worldFlushTasks.delete(task)
  }

  function dispose() {
    if (rafId !== null) window.cancelAnimationFrame(rafId)
    rafId = null
    frameRequested = false
    tasks.clear()
    worldFlushTasks.clear()
    continuousWorldUpdates = false
    worldDirty = true
    interacting = false
    frameTimeEma = null
    lastFrameAt = null
  }

  return {
    stats,
    refreshPixelRatio,
    requestRender,
    renderNow,
    markSceneChanged,
    flushWorldMatrices,
    setContinuousWorldUpdates,
    setInteracting,
    addFrameTask,
    addWorldFlushTask,
    dispose,
  }
}
